/**
 * Shared Service Facility Form
 *
 * Presentational form component for creating and editing service facilities.
 * Service facilities represent where care was rendered (CMS-1500 Box 32).
 */

import { Box, Grid, TextField, Typography } from '@mui/material';

export interface ServiceFacilityFormData {
  display_name: string;
  address: {
    line1: string;
    line2: string;
    city: string;
    state: string;
    zip: string;
  };
  facility_npi: string;
  facility_other_id: string;
}

export const initialServiceFacilityFormData: ServiceFacilityFormData = {
  display_name: '',
  address: {
    line1: '',
    line2: '',
    city: '',
    state: '',
    zip: '',
  },
  facility_npi: '',
  facility_other_id: '',
};

interface ServiceFacilityFormProps {
  data: ServiceFacilityFormData;
  onChange: (field: keyof Omit<ServiceFacilityFormData, 'address'>, value: string) => void;
  onAddressChange: (field: keyof ServiceFacilityFormData['address'], value: string) => void;
  errors?: Partial<Record<string, string>>;
}

export function ServiceFacilityForm({
  data,
  onChange,
  onAddressChange,
  errors = {},
}: ServiceFacilityFormProps) {
  return (
    <Grid container spacing={2}>
      {/* Display Name */}
      <Grid item xs={12}>
        <TextField
          label="Facility Name"
          value={data.display_name}
          onChange={e => onChange('display_name', e.target.value)}
          fullWidth
          required
          error={!!errors.display_name}
          helperText={errors.display_name || 'Name of this service location (min 2 characters)'}
        />
      </Grid>

      {/* Address */}
      <Grid item xs={12}>
        <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>
          Facility Address
        </Typography>
      </Grid>

      <Grid item xs={12}>
        <TextField
          label="Address Line 1"
          value={data.address.line1}
          onChange={e => onAddressChange('line1', e.target.value)}
          fullWidth
          required
          error={!!errors.address}
        />
      </Grid>

      <Grid item xs={12}>
        <TextField
          label="Address Line 2"
          value={data.address.line2}
          onChange={e => onAddressChange('line2', e.target.value)}
          fullWidth
          helperText="Suite, unit, etc. (optional)"
        />
      </Grid>

      <Grid item xs={12} sm={4}>
        <TextField
          label="City"
          value={data.address.city}
          onChange={e => onAddressChange('city', e.target.value)}
          fullWidth
          required
          error={!!errors.address}
        />
      </Grid>

      <Grid item xs={12} sm={4}>
        <TextField
          label="State"
          value={data.address.state}
          onChange={e => onAddressChange('state', e.target.value)}
          fullWidth
          required
          error={!!errors.address}
          helperText="2-letter code (e.g., CA)"
          inputProps={{ maxLength: 2 }}
        />
      </Grid>

      <Grid item xs={12} sm={4}>
        <TextField
          label="ZIP Code"
          value={data.address.zip}
          onChange={e => onAddressChange('zip', e.target.value)}
          fullWidth
          required
          error={!!errors.address}
          helperText="XXXXX or XXXXX-XXXX"
        />
      </Grid>

      {/* Optional Identifiers */}
      <Grid item xs={12}>
        <Box sx={{ mt: 1 }}>
          <Typography variant="subtitle2" gutterBottom>
            Optional Identifiers
          </Typography>
        </Box>
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextField
          label="Facility NPI"
          value={data.facility_npi}
          onChange={e => onChange('facility_npi', e.target.value)}
          fullWidth
          error={!!errors.facility_npi}
          helperText={errors.facility_npi || '10-digit NPI (if facility has its own)'}
          inputProps={{ maxLength: 10 }}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextField
          label="Facility Other ID"
          value={data.facility_other_id}
          onChange={e => onChange('facility_other_id', e.target.value)}
          fullWidth
          helperText="Other facility identifier (optional)"
        />
      </Grid>
    </Grid>
  );
}
